import { Component, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { AnonymousFeedbackService } from '../../services/anonymous-feedback.service';
import { ResponseBase } from '../../constant/response';
import { ExportExcel } from '../../constant/ExcelExport';
import { RoleEnum } from '../../enums/role.enum';


interface AnonymousFeedback {
  feedback: string;
  createdBy: number | null;
}

@Component({
  selector: 'app-anonymous-feedback',
  templateUrl: './anonymous-feedback.component.html',
  styleUrl: './anonymous-feedback.component.css',
  standalone: false
})
export class AnonymousFeedbackComponent {
  @ViewChild(FormGroupDirective) feedbackFormDirective?: FormGroupDirective;

  feedbackForm: FormGroup;
  isSubmitting = false;
  isExcelExporting = false;
  isAdmin = false;
  userId: number | null = null;
  private readonly feedbackLimitMessage = 'You have reached today\'s feedback limit. Please try again tomorrow.';

  constructor(
    private formBuilder: FormBuilder,
    private feedbackService: AnonymousFeedbackService,
    private snackBar: MatSnackBar,
    private router: Router,
    private exportData: ExportExcel
  ) {
    this.feedbackForm = this.formBuilder.group({
      feedback: ['', [Validators.required, Validators.maxLength(2000)]]
    });
    if (typeof sessionStorage !== 'undefined') {
      this.isAdmin = Number(sessionStorage.getItem('roleId')) === RoleEnum.ADMIN;
      const userData = sessionStorage.getItem('userData');
      this.userId = userData ? JSON.parse(userData)?.id ?? null : null;
    }
  }

  onSubmit(): void {
    if (this.isAdmin) {
      this.snackBar.open('Admin cannot submit anonymous feedback.', 'Close', { duration: 3000 });
      return;
    }

    if (this.feedbackForm.invalid) {
      this.feedbackForm.markAllAsTouched();
      return;
    }

    const feedback = this.feedbackForm.get('feedback')?.value?.trim();
    if (!feedback) {
      this.feedbackForm.get('feedback')?.setErrors({ required: true });
      this.feedbackForm.markAllAsTouched();
      this.snackBar.open('Please enter feedback before submitting.', 'Close', { duration: 3000 });
      return;
    }

    const payload: AnonymousFeedback = {
      feedback,
      createdBy: this.userId
    };

    this.isSubmitting = true;
    this.feedbackService.create(payload).subscribe({
      next: (res: ResponseBase<AnonymousFeedback>) => {
        if (res.status !== 200) {
          this.handleSubmitValidation(res.message);
          this.isSubmitting = false;
          return;
        }

        this.snackBar.open(res.message || 'Feedback submitted successfully.', 'Close', { duration: 3000 });
        this.feedbackFormDirective?.resetForm({
          feedback: ''
        });
        this.isSubmitting = false;
        this.router.navigate(['/home/anonymous-feedback/my']);
      },
      error: (err: HttpErrorResponse) => {
        this.handleSubmitValidation(this.getErrorMessage(err));
        this.isSubmitting = false;
      }
    });
  }

  exportFeedback(): void {
    if (!this.isAdmin) {
      this.snackBar.open('Only admin can export feedback.', 'Close', { duration: 3000 });
      return;
    }

    this.isExcelExporting = true;
    this.feedbackService.export().subscribe({
      next: (res: ResponseBase<string>) => {
        if (res.responseObject) {
          saveAs(this.exportData.base64ToBlob(res.responseObject), `anonymousFeedback_${this.getFormattedDate()}`);
          this.snackBar.open('Feedback exported successfully.', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'No feedback found.', 'Close', { duration: 3000 });
        }
        this.isExcelExporting = false;
      },
      error: (err) => {
        this.snackBar.open(err?.message || 'Failed to export feedback.', 'Close', { duration: 3000 });
        this.isExcelExporting = false;
      }
    });
  }

  private getFormattedDate(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year}_${hours}-${minutes}.xlsx`;
  }

  private handleSubmitValidation(message: string): void {
    const validationMessage = message || 'Failed to submit feedback.';

    if (this.isFeedbackLimitMessage(validationMessage)) {
      this.feedbackForm.get('feedback')?.setErrors({ dailyLimit: true });
      this.feedbackForm.get('feedback')?.markAsTouched();
      return;
    }

    this.snackBar.open(validationMessage, 'Close', { duration: 3000 });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err?.error === 'string') {
      return err.error;
    }

    return err?.error?.message || err?.message || '';
  }

  private isFeedbackLimitMessage(message: string): boolean {
    const normalizedMessage = message.toLowerCase();
    return normalizedMessage.includes('feedback') &&
      ( normalizedMessage.includes('limit') || normalizedMessage.includes('exceed') || normalizedMessage.includes('exceeded') );
  }
}
